import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../services/api';
import { PageHeader, KpiCard, Loading, BadgeSituacao, BadgePrazo, EmptyState } from '../components/ui';
import { ModalDebitos } from '../components/DebitosPedidos';
import { ProjecaoCaixa } from '../components/ProjecaoCaixa';
import { formatarMoeda, formatarData } from '../lib/format';
import {
  Clock, Package, CheckCircle2, Scissors, Zap, Wallet,
  ArrowUpRight, ArrowDownRight, AlertTriangle, PackagePlus,
  ArrowRight, CalendarClock, Banknote, Store, HandCoins,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function DashboardPage() {
  const [debitosAberto, setDebitosAberto] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardApi.central().then(r => r.data),
  });

  if (isLoading) return <Loading altura="h-96" />;

  const { pedidos, financeiro, costura, loja, debitos } = data || {};
  const saldoPositivo = (financeiro?.saldoHoje || 0) >= 0;

  return (
    <div className="space-y-8">
      <PageHeader
        titulo="Central de Operações"
        descricao={format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
      >
        <Link to="/entrada-rapida" className="btn-secondary">
          <Zap className="w-4 h-4 text-amber-500" />
          Entrada Rápida
        </Link>
        <Link to="/pedidos/novo" className="btn-primary">
          <PackagePlus className="w-4 h-4" />
          Novo Pedido
        </Link>
      </PageHeader>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
        <KpiCard titulo="Aguardando Entrada" valor={pedidos?.aguardandoEntrada || 0} icon={Clock} tom="amber" detalhe="pedidos na fila" />
        <KpiCard titulo="Em Produção" valor={pedidos?.emProducao || 0} icon={Package} tom="blue" detalhe="pedidos ativos" />
        <KpiCard titulo="Prontos p/ Entrega" valor={pedidos?.prontos || 0} icon={CheckCircle2} tom="green" detalhe="aguardando retirada" />
        <KpiCard titulo="Lotes de Costura" valor={costura?.lotesAtivos || 0} icon={Scissors} tom="purple" detalhe="lotes em aberto" />
        <KpiCard
          titulo="Loja Virtual"
          valor={loja?.pagosHoje || 0}
          icon={Store}
          tom="brand"
          detalhe={`${formatarMoeda(loja?.valorPagoHoje)} hoje · ${loja?.aguardandoPagamento || 0} aguardando pgto`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Coluna principal */}
        <div className="xl:col-span-2 space-y-6">
          {/* Pedidos com débitos pendentes */}
          <button
            onClick={() => setDebitosAberto(true)}
            className="w-full text-left card transition-all duration-200 hover:shadow-pop hover:-translate-y-0.5 group"
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3.5 min-w-0">
                <span className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                  <HandCoins className="w-5 h-5 text-red-600" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-900">
                    Pedidos com Débitos Pendentes
                  </p>
                  <p className="text-[13px] text-ink-500 mt-0.5">
                    {debitos?.quantidade || 0} pedido(s) aguardando recebimento — clique para ver e dar baixa
                  </p>
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="font-display text-2xl font-bold text-red-600">
                  {formatarMoeda(debitos?.valorEmAberto)}
                </p>
                <p className="text-2xs font-semibold text-ink-400 uppercase tracking-wider flex items-center justify-end gap-1 group-hover:text-brand-600 transition-colors">
                  Ver lista <ArrowRight className="w-3 h-3" />
                </p>
              </div>
            </div>
          </button>

          {/* Financeiro do dia */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-brand-600" />
                </span>
                Financeiro de Hoje
              </h2>
              <Link to="/financeiro" className="text-[13px] font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">
                Ver financeiro <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl bg-emerald-50/70 ring-1 ring-inset ring-emerald-600/10 p-4">
                <div className="flex items-center gap-2 text-emerald-700">
                  <ArrowUpRight className="w-4 h-4" />
                  <p className="text-2xs font-semibold uppercase tracking-wider">Entradas</p>
                </div>
                <p className="font-display text-xl font-bold text-emerald-800 mt-2">
                  {formatarMoeda(financeiro?.entradasHoje)}
                </p>
              </div>
              <div className="rounded-xl bg-red-50/70 ring-1 ring-inset ring-red-600/10 p-4">
                <div className="flex items-center gap-2 text-red-700">
                  <ArrowDownRight className="w-4 h-4" />
                  <p className="text-2xs font-semibold uppercase tracking-wider">Saídas</p>
                </div>
                <p className="font-display text-xl font-bold text-red-800 mt-2">
                  {formatarMoeda(financeiro?.saidasHoje)}
                </p>
              </div>
              <div className={`rounded-xl p-4 ring-1 ring-inset ${saldoPositivo ? 'bg-brand-50/80 ring-brand-600/10' : 'bg-red-50/70 ring-red-600/10'}`}>
                <div className={`flex items-center gap-2 ${saldoPositivo ? 'text-brand-700' : 'text-red-700'}`}>
                  <Banknote className="w-4 h-4" />
                  <p className="text-2xs font-semibold uppercase tracking-wider">Saldo</p>
                </div>
                <p className={`font-display text-xl font-bold mt-2 ${saldoPositivo ? 'text-brand-800' : 'text-red-800'}`}>
                  {formatarMoeda(financeiro?.saldoHoje)}
                </p>
              </div>
            </div>
          </div>

          {/* Pedidos que precisam de atenção */}
          <div className="card">
            <div className="card-header">
              <h2 className="card-title flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                </span>
                Pedidos que Precisam de Atenção
              </h2>
            </div>
            {pedidos?.urgentes?.length > 0 ? (
              <div className="space-y-2.5">
                {pedidos.urgentes.map((p: any) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between gap-4 p-4 rounded-xl bg-amber-50/60 ring-1 ring-inset ring-amber-600/15"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-ink-900 truncate">{p.cliente?.nome}</p>
                      <p className="text-[13px] text-ink-500 mt-0.5 flex items-center gap-1.5">
                        <CalendarClock className="w-3.5 h-3.5" />
                        Prazo {formatarData(p.prazo)}
                      </p>
                    </div>
                    <BadgePrazo tipo={p.tipoPrazo} />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={CheckCircle2}
                titulo="Tudo sob controle"
                descricao="Nenhum pedido com prazo crítico no momento."
              />
            )}
          </div>
        </div>

        {/* Ações rápidas */}
        <div className="card h-fit">
          <h2 className="card-title flex items-center gap-2.5 mb-5">
            <span className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <Zap className="w-4 h-4 text-amber-500" />
            </span>
            Ações Rápidas
          </h2>
          <div className="space-y-2">
            <AcaoRapida to="/entrada-rapida" icon={Zap} label="Entrada Rápida" descricao="Lançar venda do dia" tom="text-amber-600 bg-amber-50" />
            <AcaoRapida to="/pedidos/novo" icon={PackagePlus} label="Novo Pedido" descricao="Cadastrar encomenda" tom="text-brand-600 bg-brand-50" />
            <AcaoRapida to="/financeiro" icon={Wallet} label="Novo Lançamento" descricao="Despesa ou receita" tom="text-red-600 bg-red-50" />
            <AcaoRapida to="/pedidos" icon={Package} label="Ver Pedidos" descricao="Acompanhar situação" tom="text-blue-600 bg-blue-50" />
            <AcaoRapida to="/costura" icon={Scissors} label="Costura" descricao="Lotes e costureiras" tom="text-violet-600 bg-violet-50" />
          </div>
        </div>
      </div>

      {/* Projeção de caixa */}
      <ProjecaoCaixa />

      {/* Últimos pedidos */}
      <div className="table-shell">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-100">
          <h2 className="card-title">Últimos Pedidos</h2>
          <Link to="/pedidos" className="text-[13px] font-semibold text-brand-600 hover:text-brand-700 flex items-center gap-1">
            Ver todos <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {pedidos?.ultimos?.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Cliente</th>
                  <th>Valor</th>
                  <th>Prazo</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.ultimos.map((p: any) => (
                  <tr key={p.id}>
                    <td className="font-semibold text-ink-900">#{p.numero}</td>
                    <td>{p.cliente?.nome}</td>
                    <td className="font-semibold">{formatarMoeda(p.valorTotal)}</td>
                    <td className="text-ink-500">{formatarData(p.prazo)}</td>
                    <td><BadgeSituacao situacao={p.situacao} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={Package} titulo="Nenhum pedido ainda" descricao="Os pedidos cadastrados aparecerão aqui." />
        )}
      </div>

      <ModalDebitos aberto={debitosAberto} onFechar={() => setDebitosAberto(false)} />
    </div>
  );
}

function AcaoRapida({
  to, icon: Icon, label, descricao, tom,
}: {
  to: string; icon: any; label: string; descricao: string; tom: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3.5 p-3 rounded-xl border border-transparent transition-all duration-150 hover:border-ink-200 hover:bg-ink-50/60 group"
    >
      <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tom}`}>
        <Icon className="w-[18px] h-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-ink-800">{label}</span>
        <span className="block text-xs text-ink-400">{descricao}</span>
      </span>
      <ArrowRight className="w-4 h-4 text-ink-300 transition-transform group-hover:translate-x-0.5 group-hover:text-ink-500" />
    </Link>
  );
}
